import Layout from '@/components/layout';
import { config } from '@/config';
import { ONBOARDING_STEP } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { useGetShopThemesQuery, useGetWidgetAppearanceTextDetailQuery, useUpdateGeneralSettingMutation } from '@/redux/query';
import { handleSaveOnboardingSettings, onboardingSettingSelector } from '@/redux/slice/onboarding.slice';
import { handleSaveWidgetTextDetail, handleSaveWidgetTextDetailBackup } from '@/redux/slice/widgetText.slice';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import OnboardingFinish from './component/OnboardingFinish';
import OnboardingTheme from './component/OnboardingTheme';
import OnboardingWelcome from './component/OnboardingWelcome';

const Onboarding = () => {
  const { t } = useTranslation('onboarding');
  const onboardingSettings = useSelector(onboardingSettingSelector);
  const [updateGeneralSetting] = useUpdateGeneralSettingMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { data: widgetAppearanceTextDetail } = useGetWidgetAppearanceTextDetailQuery({
    shop: config.shop,
    defaultLanguage: true,
  });
  const {
    data: shopThemesData,
    isLoading: isLoadingShopThemes,
    refetch: refetchShopThemes,
  } = useGetShopThemesQuery({ shop: config.shop, cache: 'no-cache' }, { refetchOnFocus: true });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          refetchShopThemes();
        }, 0);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchShopThemes]);

  useEffect(() => {
    if (!isFirstLoad) return;
    const mainThemeId = shopThemesData?.find((item) => item.role === 'MAIN')?.id;
    if (!mainThemeId) return;
    dispatch(handleSaveOnboardingSettings({ theme: mainThemeId }));
    setIsFirstLoad(false);
  }, [dispatch, isFirstLoad, shopThemesData]);

  const skipSetup = () => {
    updateGeneralSetting({ shop: config.shop, displayOnboarding: false, onboardingStep: onboardingSettings.step });
    navigate(PATH.DEFAULT.pathname);
  };

  const renderOnboardingStep = () => {
    switch (onboardingSettings.step) {
      case ONBOARDING_STEP.WELCOME:
        return (
          <Layout title={t('page.title')}>
            <OnboardingWelcome shopThemesData={shopThemesData} isLoadingShopThemes={isLoadingShopThemes} handleSkip={skipSetup} />
          </Layout>
        );
      case ONBOARDING_STEP.EMBEDDED_APP:
        return (
          <Layout
            title={t('embedded.title')}
            backAction={{
              content: '',
              onAction: () => {
                dispatch(handleSaveOnboardingSettings({ step: ONBOARDING_STEP.WELCOME }));
              },
            }}
          >
            <OnboardingTheme shopThemesData={shopThemesData} isLoadingShopThemes={isLoadingShopThemes} handleSkip={skipSetup} />
          </Layout>
        );
      case ONBOARDING_STEP.FINISH:
        return (
          <Layout
            title={t('finish.title')}
            backAction={{
              content: '',
              onAction: () => {
                dispatch(handleSaveOnboardingSettings({ step: ONBOARDING_STEP.EMBEDDED_APP }));
              },
            }}
          >
            <OnboardingFinish />
          </Layout>
        );
      default:
        return (
          <Layout title={t('page.title')}>
            <OnboardingWelcome shopThemesData={shopThemesData} isLoadingShopThemes={isLoadingShopThemes} />
          </Layout>
        );
    }
  };

  useEffect(() => {
    if (widgetAppearanceTextDetail?.data) {
      dispatch(handleSaveWidgetTextDetail(widgetAppearanceTextDetail?.data));
      dispatch(handleSaveWidgetTextDetailBackup(widgetAppearanceTextDetail?.data));
    }
  }, [dispatch, widgetAppearanceTextDetail?.data]);

  return renderOnboardingStep();
};

export default Onboarding;
